import { VEHICLE } from './VehicleDamage.js';

/**
 * @file THE WIRING — what plays when. `engine/audio/Sfx.js` knows how to make a
 * noise and knows nothing about this game; this file knows the game and cannot
 * make a noise. Everything between the two is a slug string.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO WEB AUDIO, NO THREE.JS, NO GAME CONTENT
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE import — `VEHICLE`, for the two impact thresholds, because inventing a
 * third "this counts as a crash" number here is the `links.js`/`countryroads.js`
 * mirroring trap. Everything else (`sfx`, `bus`, `player`, `rig`, `driving`,
 * `traffic`, `wanted`, `drone`, `interiors`, `heightAt`) is
 * INJECTED and **every one of them may be null**: the `VehicleDamage.js` /
 * `Drone.js` / `Speech.js` discipline. A `createGameAudio()` with nothing
 * injected is a legal, fully-stepping system that subscribes to nothing and
 * plays nothing — which is what lets `tests/gameaudio.test.mjs` drive the whole
 * table in node with duck types, no GPU, no browser and no `AudioContext`.
 *
 * It draws NO dice — no `Math.random` (rule 4) and no seeded stream either,
 * because nothing here needs to choose anything: variant selection and rate
 * jitter belong to the mixer, which owns one private generator for them and
 * documents it as not a sim stream. So this file cannot perturb a stage and
 * cannot move the hero checksum even if it WERE built in hero mode. It holds no
 * clock of the simulation's either; the one clock it does hold is a REAL one,
 * and the next section is why.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THE TWO-HOOK SPLIT IS THE DESIGN, AND IT IS ABOUT THE FREEZE
 * ─────────────────────────────────────────────────────────────────────────────
 * `Engine#_fixed` calls `sys.update(dt)` on the fixed step and `Engine#_render`
 * calls `sys.render(dt, alpha)` at display rate. **Five overlays can freeze the
 * loop** (`ui/hud/freezeOwner.js` — pause menu, cutscene, shop panel, phone
 * panel, phone call), and a freeze is `loop.setTimeScale(0)`: the accumulator
 * stops filling, so `onFixed` is never called again, while `onRender` keeps
 * being called every vsync. So:
 *
 *  • **`update(dt)` is the SIM-COUPLED polling** — footsteps, engine loops, the
 *    drone loop, where the siren is standing. All of it describes a world that
 *    is not moving while an overlay is up, and all of it SHOULD stop.
 *  • **`render(dt)` is `sfx.update()` and the listener push** — voice reaping
 *    and loop fades. These MUST keep running while frozen, or a phone ringing
 *    over an open pause menu never fades out and a loop stopped on the very
 *    frame the freeze landed hangs open for ever with its fade half-finished.
 *
 * ⚠ AND THE `dt` `render` IS HANDED IS EXACTLY ZERO WHILE FROZEN, because
 * `createLoop` multiplies the frame delta by `timeScale` before it passes it on.
 * Feeding that to `sfx.update()` would defeat the split entirely. So this file
 * keeps a REAL wall clock ({@link createGameAudio}'s `now`, milliseconds, the
 * `performance.now` convention) and bills `sfx` the real span between renders.
 *
 * That is not a workaround, it is the correct reading: the audio backend
 * schedules its ramps against `AudioContext.currentTime`, which is real seconds
 * and does not know what a time scale is. A mixer aged on scaled time would
 * drift out of agreement with the sound that is actually coming out of the
 * speakers. The clock is INJECTED so a suite can drive it exactly.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THE RADIO'S CLOCK IS THE RENDER CLOCK, AND THAT IS THE WHOLE FEATURE
 * ─────────────────────────────────────────────────────────────────────────────
 * A station is ALWAYS ON AIR. Getting into a car OPENS A WINDOW onto it; it does
 * not start it, and stepping out does not stop it — out at 0:30, back ten
 * seconds later, and the track resumes at 0:40. `systems/Radio.js` holds no
 * clock of its own (it is told `dt`), so somebody has to tell it, and the
 * somebody is `render`, billed THE SAME REAL SPAN `sfx.update` already gets.
 *
 * ⚠ NOT `update`, and this is not a stylistic echo of the split above — it is
 * the same failure. A player who opens the pause menu for a minute must come
 * back to the track a minute further on; a transmitter that stopped dead under a
 * freeze would pick up mid-bar and the only thing the feature is selling is
 * gone. The sim is what stops. Neither the mixer's ramps nor a radio station is
 * part of it.
 *
 * ⚠ AND THIS FILE DECIDES NOTHING ABOUT WHAT IS PLAYING. It picks no track, it
 * cannot tell one station from another, and — the one that is easy to get wrong
 * — IT DOES NOT BUILD THE URL. `'radio:track'` carries a `file` that is already
 * the playable address; joining a base onto it a second time here would be two
 * places computing one string, the `hurtPlayer(amount, lethal)` family, and the
 * failure mode is a 404 nobody can locate because both halves look right.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE LISTENER IS THE PLAYER, NEVER `interiors.focus`
 * ─────────────────────────────────────────────────────────────────────────────
 * A shop is a real room 100 m under the island and `interiors.focus` is the door
 * ring up on the pavement. Anchoring the listener there would pipe the whole
 * street — traffic, gunfire, sirens — into a room the player is standing in
 * under solid rock, and would put the shopkeeper beside them 100 m away. This is
 * the rule `docs/dialogue.md` states for the speech distance test and
 * `src/game/sfx.js#SFX_TUNE.teleportDist` restates for the mixer, and it is the
 * same rule for the same reason.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE DECIDES, AND WHAT IT REFUSES TO DECIDE
 * ─────────────────────────────────────────────────────────────────────────────
 * It decides WHICH SLUG and WHERE. It decides nothing about loudness beyond a
 * relative multiplier on one event (`car_crash`, scaled by how expensive the
 * shunt was), because the mix is `src/game/sfx.js` and re-tuning a level here
 * would be a second mix table nobody can find. It never asks whether a sound is
 * audible, never counts voices and never looks at a distance: `maxDist` is a
 * CULL inside the mixer, evaluated before anything is allocated, and duplicating
 * that test here would be two places computing one question — the
 * `hurtPlayer(amount, lethal)` family.
 *
 * ⚠ IT PUBLISHES NOTHING. Every channel below is one this game already carried,
 * and several of them had zero subscribers before this file existed, which is
 * exactly why they were free. Nothing was added to the bus for sound.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WIRING TABLE
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 *  SUBSCRIPTIONS
 *  'car:exploded'   {x,y,z}        -> explosion            (positional)
 *  'prop:broken'    {kind,x,z}     -> prop_smash           (positional)
 *  'shop:bought'                   -> cash                 (2D, ui bus)
 *  'mission:event'  {type}         -> complete: cash + ui_notify
 *                                     started/failed: ui_notify
 *  'drone:delivered'               -> ui_notify
 *  'player:wasted'                 -> siren_ambulance loop ON
 *  'player:respawn'                -> siren_ambulance loop OFF
 *  'wanted:level'   {level}        -> siren_police loop ON/OFF (sited in update)
 *  'phone:ring'     {waiting}      -> phone_ring loop ON/OFF
 *  'phone:open|view|order|callback'-> phone_key
 *  'phone:call'     {shot}         -> text_notify, ONLY on shot 0
 *  'player:vehicle' {in}           -> car_start, keyed on the fleet handle
 *                                     ...and the radio window: enter / exit+fade
 *  'radio:track'    {file,offset}  -> sfx.music.play(file, offset)  (⚠ verbatim)
 *  'screen:fade'    {black}        -> door, on the BLACK edge
 *  'weather'        {rain}         -> rain loop gain (hysteresis, see RAIN_ON)
 *
 *  CALL SITES (Gameplay.js hands these to us; see each method)
 *  playerShot()                    -> gunshot at the player
 *  npcShot(x,y,z)                  -> gunshot at the muzzle
 *  groanAt(x,y,z)                  -> groan (NPC hurt or killed)
 *  playerHurt()                    -> groan at the player
 *  carImpact(kind, dv)             -> car_crash, gain from the damage it cost
 *
 *  POLLED IN update(dt)
 *  the player's footsteps          -> footstep, one per PI of the walk phase
 *  the player's car + nearest 3    -> car_engine loops, keyed on the handle
 *  drone.active                    -> drone loop
 *  nearest police CAR              -> where the siren_police loop stands
 *                                     ...and NO car is NO siren, not a fallback
 *
 *  DRIVEN IN render(dt)  — the REAL span, never the sim's
 *  radio.update(span)              -> the station's on-air clock
 * ```
 *
 * @module engine/systems/GameAudio
 */

/** 2 PI. */
const TAU = Math.PI * 2;

/**
 * ⚠ ONE FOOTFALL PER PI OF WALK PHASE, AND IT IS DERIVED, NOT CHOSEN.
 * `actors/WalkWobble.js` rocks the sprite at `sin(phase)` and bobs it at
 * `cos(2 * phase)` — "one tilt cycle = lean left, lean right = TWO steps, and
 * the body rises once per step", in its own words. So the bob runs at twice the
 * tilt rate and a footfall is half a tilt cycle: PI. Anything else here puts the
 * sound off the picture, which is the one thing a footstep cannot survive.
 */
const STEP_PHASE = Math.PI;

/**
 * The handful of numbers this file owns. Everything else it needs is read from
 * the system that already owns it — `VEHICLE` for the impact thresholds, the
 * sound table for every gain, `WalkWobble` for the gait.
 */
export const AUDIO = Object.freeze({
  /**
   * `WalkWobble#amp` below which the player is not walking. The wobble freezes
   * its PHASE when `moving` is false and damps only the AMPLITUDE, so a stopped
   * player produces no phase crossings and this gate is belt and braces — but
   * it is the belt that covers a player being shoved, carried or slid to a stop
   * with a stale phase still ticking under them.
   */
  stepAmp: 0.1,
  /**
   * How many AI cars idle audibly, on top of the player's own. Three, because
   * `src/game/sfx.js` caps `car_engine` at `maxVoices: 4` and the player's car
   * is always the first of them — so this is that cap read from the other end,
   * and a fourth AI car would be allocated only to be stolen by the next one.
   * ⚠ There is deliberately no radius here: `car_engine.maxDist` is the cull and
   * it lives in the mix table. A second distance test in this file is the
   * two-places-computing bug.
   */
  engineCars: 3,
  /**
   * Rain hysteresis, on the 0..1 `'weather'` amount. `Weather` publishes a
   * DAMPED value at ~5 Hz (`WEATHER_RAMP.rain` 0.3, so a shower takes ~10 s to
   * arrive), not a discrete start/stop — so a single threshold would start and
   * stop the bed a dozen times as the ramp crawls across it. On is higher than
   * off, and the gap is what makes the bed decide once.
   */
  rainOn: 0.06,
  rainOff: 0.02,
  /**
   * Fraction of a car's health an impact has to cost to be a FULL-VOLUME crash.
   * 0.3 is the 12 m/s wall impact `VEHICLE.wallDamage`'s own comment cites as
   * the crash "which stops the car dead" — ~30 hp of 100. Everything softer
   * scales down toward {@link crashMinGain}; nothing scales up, because a
   * multiplier over 1 would push `car_crash` past the mix the table authored.
   */
  crashFullFrac: 0.3,
  /**
   * The quietest a qualifying crash may be. A scrape that fails
   * `VEHICLE.wallImpactMin` is SILENT (see {@link createGameAudio}'s
   * `carImpact`); one that passes it has been judged by the damage model to be
   * a real hit, and a real hit you cannot hear reads as the sound being broken.
   */
  crashMinGain: 0.35,
  /**
   * Longest real span, seconds, this file will bill `sfx.update()` for in one
   * render. The same guard and the same value as `core/loop.js`'s
   * `MAX_FRAME_DT`: a tab that was hidden for a minute must not age every voice
   * in the pool out at once on the frame it comes back.
   */
  maxRenderDt: 0.25,
});

/**
 * @param {any} n
 * @returns {boolean} A real, usable number — the `Wallet.js#usable` guard. A
 *   `NaN` out of a malformed injection must never reach a pan calculation.
 */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * @returns {number} Milliseconds on whatever real clock this environment has.
 *   ⚠ This is the DEFAULT only; `createGameAudio` takes `now` so a suite can
 *   drive the render clock exactly. See the header for why the clock is real.
 */
const defaultNow = () =>
  (typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now());

/**
 * THE WIRING. Subscribes, polls, and decides what plays when.
 *
 * @param {Object} [o]
 * @param {any} [o.sfx] `engine/audio/Sfx.js`. Duck-typed at every call
 *   (`sfx?.play?.(…)`), so a facade that shipped without a method, or no facade
 *   at all, is a silent game rather than a crash.
 * @param {any} [o.bus] Every channel in the table above is a SUBSCRIPTION. This
 *   file publishes NOTHING.
 * @param {any} [o.player] `position`, `dead` and `wobble` — the listener, the
 *   footsteps and the player's own groan.
 * @param {any} [o.rig] `CameraRig`, for `yaw`. The listener's facing is the
 *   CAMERA's, never the player's: the pan is about the screen.
 * @param {any} [o.driving] `inCar`, `fleetHandle`, `body` — the player's own
 *   engine loop and the "am I on foot" half of the footstep gate.
 * @param {any} [o.traffic] The AI pool, read through its public parallel arrays
 *   (`top`, `handle`, `x`, `y`, `z`) exactly as `VehicleDamage` reads them.
 *   ⚠ No accessor was added to `Traffic.js` for this: a live driving slot is
 *   `handle[i] >= 0` (`_deactivate` sets it to −1), which is the whole test.
 * @param {any} [o.wanted] TWO readings, and they answer different halves of the
 *   siren. `'wanted:level'` off the BUS says WHETHER one is running;
 *   `wanted.response.nearestSiren(x, z, out)` says WHERE — the nearest car this
 *   file's opposite number is already flashing the lightbar of. Duck-typed the
 *   whole way down, so the heat-curve-only construction (no manager, no
 *   traffic, hence no `response`) is a silent siren rather than a crash. See
 *   `siren`.
 * @param {any} [o.drone] `active`/`x`/`y`/`z` — the rotor loop.
 * @param {any} [o.radio] `systems/Radio.js` — the station model. Null on every
 *   level without a radio table, and then not one radio branch below runs: no
 *   `enter`/`exit`, no `'radio:track'` subscription, nothing told a clock. It is
 *   TOLD its `dt` from `render` — see the header, that is the feature.
 * @param {{gain?: number, fade?: number}|null} [o.radioTune] `game/radio.js#
 *   RADIO_TUNE`, injected by the ROUTE because it is CONTENT: how loud the radio
 *   sits under the gunfire, and how long it takes to arrive and to leave.
 *   ⚠ INJECTED RATHER THAN TABLED IN {@link AUDIO}, for the
 *   `links.js`/`countryroads.js` reason — the two numbers already exist in
 *   `src/game/radio.js` beside the stations they are about, this file may not
 *   import that directory, and a second copy of a pair that must move together
 *   is how one of them stops moving. Absent, the facade keeps its own defaults.
 * @param {any} [o.interiors] `focus`, for where a door is. NOT for the listener.
 * @param {((x: number, z: number) => number)|null} [o.heightAt] Ground height,
 *   for the one event that arrives without a Y (`'prop:broken'`). Absent reads
 *   as sea level.
 * @param {(() => number)|null} [o.now] Real milliseconds. See the header — this
 *   is the render clock, and it is real on purpose.
 * @returns {Object} `{name, update, render, stats, dispose}` plus the five
 *   call-site methods `Gameplay.js` uses.
 */
export function createGameAudio(o = {}) {
  const {
    sfx = null,
    bus = null,
    player: player0 = null,
    rig = null,
    driving = null,
    traffic = null,
    wanted = null,
    drone = null,
    radio = null,
    radioTune = null,
    interiors = null,
    heightAt = null,
    now = null,
  } = o ?? {};

  const clock = typeof now === 'function' ? now : defaultNow;

  /**
   * ⚠ REASSIGNABLE. `Gameplay` rebuilds the `Player` when the chibi sprite
   * decodes (a `SpriteNodeMaterial` bakes its texture at construction, so a
   * "swap" is a new actor), and it repoints every system that captured the old
   * one. Miss this and the LISTENER freezes at the origin the sprite happened
   * to decode at, which is the whole spatial mix gone silently wrong.
   */
  let player = player0;

  let disposed = false;

  // ── the listener ───────────────────────────────────────────────────────────
  /** Last pushed listener, for `stats()` and for nothing else. */
  const ear = { x: 0, y: 0, z: 0, yaw: 0 };

  // ── footsteps ──────────────────────────────────────────────────────────────
  /** `WalkWobble#phase` as of the previous step. */
  let lastPhase = 0;
  /** Radians of phase banked toward the next footfall. */
  let stepAcc = 0;

  // ── loops we are holding open ──────────────────────────────────────────────
  /** Fleet handles currently idling. Swapped with {@link engineWant} each step,
   *  so the reconcile allocates nothing. */
  let engineKeys = [];
  /** Scratch for the step's desired set. */
  let engineWant = [];
  /** The nearest-N window over the AI pool. Fixed length, allocated once — the
   *  `doorMarkers` scratch idiom in `Gameplay.js`. */
  const nearD2 = new Float64Array(AUDIO.engineCars);
  const nearH = new Int32Array(AUDIO.engineCars).fill(-1);
  const nearX = new Float64Array(AUDIO.engineCars);
  const nearY = new Float64Array(AUDIO.engineCars);
  const nearZ = new Float64Array(AUDIO.engineCars);
  let droneOn = false;
  let sirenOn = false;
  /**
   * Where the police siren is standing this step. Fixed scratch, allocated ONCE
   * and written by `wanted.response.nearestSiren` — the `nearD2`/`nearH` idiom
   * two declarations up. It is read on the frame it is written and never
   * between frames, so a stale value cannot escape: `nearestSiren` leaves it
   * untouched when nothing is flashing, and `siren` does not look at it then.
   */
  const sirenAt = { x: 0, y: 0, z: 0 };
  /**
   * Whether the police loop is currently HELD SOMEWHERE, which is not the same
   * question as {@link sirenOn} — a wanted level can be up with no car in the
   * world to hang the sound on. Its only job is to make the drop happen ONCE
   * rather than asking the facade to stop a loop that is not running on every
   * step of a ★1 patrol.
   */
  let sirenPlaced = false;
  let ambulanceOn = false;
  let ringOn = false;
  let rainOn = false;
  /** Last rain amount seen on `'weather'`. Drives the bed's live gain. */
  let rainAmt = 0;

  // ── the radio ──────────────────────────────────────────────────────────────
  /**
   * Seconds to fade the stream in and out, or `undefined` so the facade keeps
   * its own default. ⚠ `undefined` and not 0 — a hard cut on a music stream is
   * the most obvious artifact in the whole audio system, and a level built
   * without the tune should get the facade's fade, not none.
   */
  const musicFade = usable(radioTune?.fade) ? radioTune.fade : undefined;
  /** `music.play`'s opts. Allocated ONCE and never written — the fade in has to
   *  match the fade out, so both come off the one injected number. */
  const musicOpts = musicFade === undefined ? undefined : Object.freeze({ fade: musicFade });
  /** ⚠ WHETHER **WE** ASKED FOR MUSIC, which is not the same question as whether
   *  anything is playing — the `heldLoops` rule. A dev scene may drive the same
   *  facade, and taking somebody else's track down on our way out is not our
   *  business. Read by `dispose` and by nothing else. */
  let musicOn = false;
  /** How many `'radio:track'` events this file turned into a `play`. */
  let radioStarts = 0;

  // ── the render clock ───────────────────────────────────────────────────────
  /** Milliseconds at the previous render, or null before the first one. */
  let lastMs = null;

  // ── counters ───────────────────────────────────────────────────────────────
  let steps = 0;
  let frames = 0;
  let played = 0;
  let footfalls = 0;

  /** @type {Array<() => void>} */
  const unsubs = [];

  // ── the two doors onto `sfx` ───────────────────────────────────────────────

  /**
   * ONE play door, so "did this file ask for a sound" is one counter and one
   * breakpoint. It never inspects the answer: `play()` returns −1 for a slug
   * that was culled, gapped or stolen, and this file has no opinion about any
   * of those — the mixer does.
   * @param {string} slug @param {any} [opts]
   */
  const shot = (slug, opts) => {
    if (disposed) return;
    played++;
    sfx?.play?.(slug, opts);
  };

  /**
   * ONE positional play door. Folded separately from {@link shot} only because
   * every caller of it was building the same three-field object.
   * @param {string} slug @param {number} x @param {number} y @param {number} z
   * @param {any} [extra] Merged over the position — `gain`, `key`, `rate`.
   */
  const shotAt = (slug, x, y, z, extra = null) => {
    if (!usable(x) || !usable(y) || !usable(z)) return;
    shot(slug, extra ? { x, y, z, ...extra } : { x, y, z });
  };

  /**
   * The EVENT-DRIVEN loops that are currently supposed to be sounding, by
   * `slug|key`. The polled loops (engines, the drone, the siren's position) are
   * not in here: they are re-asked from scratch every frame by `update`, so they
   * already self-heal.
   *
   * ⚠⚠ **THIS MAP EXISTS BECAUSE A LOOP STARTED ON A BUS EDGE COULD BE LOST FOR
   * EVER, AND THE INTRO PHONE CALL WAS LOSING IT EVERY SINGLE SESSION.**
   * `hold()` used to be a bare `sfx.loop(...)` fired once, on the 0→1 edge of
   * `'phone:ring'`'s `waiting` count. But the browser will not create an
   * `AudioContext` before a user gesture, and **the phone rings within a second
   * or two of the level booting — before the player has clicked anything.** So
   * the call arrived with no context, no-opped exactly as designed, and then
   * `ringOn` was `true`, the edge never came again, and the ring the whole
   * feature exists for made no sound. Answering the call cleared it.
   *
   * Re-asserting every frame fixes it at the general level rather than
   * special-casing the phone: `Sfx#loop` is documented as an IDEMPOTENT
   * KEEP-ALIVE, so a re-ask for a live loop is a cheap touch and a re-ask for a
   * dead one restarts it. That also covers a loop lost to voice stealing and a
   * loop asked for while the pack was still decoding.
   *
   * @type {Map<string, {slug: string, key: any, opts: any}>}
   */
  const heldLoops = new Map();

  /** @param {string} slug @param {any} key @param {any} [opts] */
  const hold = (slug, key, opts) => {
    if (disposed) return;
    heldLoops.set(`${slug}|${key}`, { slug, key, opts });
    sfx?.loop?.(slug, key, opts);
  };

  /** @param {string} slug @param {any} key */
  const drop = (slug, key) => {
    if (disposed) return;
    heldLoops.delete(`${slug}|${key}`);
    sfx?.stopLoop?.(slug, key);
  };

  /**
   * Re-ask for every event-driven loop. Called from `render`, not `update`.
   *
   * ⚠ **`render` IS THE CORRECT HOOK AND `update` IS NOT**, for the same reason
   * the rest of the two-hook split exists: the unlock gesture can land while an
   * overlay has the sim frozen (a player who clicks the pause menu before
   * anything else), and `update` does not run there. A ring that only recovers
   * once the world is unpaused is the same bug one layer down. There are at most
   * three of these and the call is idempotent, so the per-frame cost is a Map
   * walk over a handful of entries.
   */
  const reassertLoops = () => {
    if (disposed || !sfx) return;
    for (const h of heldLoops.values()) sfx.loop?.(h.slug, h.key, h.opts);
  };

  // ── the world, read through guards ─────────────────────────────────────────

  /** @param {number} x @param {number} z @returns {number} Ground, or 0. */
  const ground = (x, z) => {
    if (typeof heightAt !== 'function') return 0;
    const h = heightAt(x, z);
    return usable(h) ? h : 0;
  };

  /**
   * ⚠ `player.position`, NEVER `interiors.focus`. See the header.
   * Pushed at the top of BOTH hooks through this one function, so the two call
   * sites cannot grow different ideas of where the player's ears are.
   */
  const pushListener = () => {
    const p = player?.position;
    ear.x = usable(p?.x) ? p.x : 0;
    ear.y = usable(p?.y) ? p.y : 0;
    ear.z = usable(p?.z) ? p.z : 0;
    ear.yaw = usable(rig?.yaw) ? rig.yaw : 0;
    sfx?.setListener?.(ear.x, ear.y, ear.z, ear.yaw);
  };

  // ── the polled half (fixed step; stops dead under a freeze) ────────────────

  /**
   * ONE FOOTFALL PER PI OF WALK PHASE — see {@link STEP_PHASE}.
   *
   * ⚠ THE PLAYER ONLY. `src/game/sfx.js#footstep` records the decision and the
   * reason: a pavement full of pedestrians would be the heaviest channel in the
   * game AND would arrive as mush, because a crowd's steps have no phase
   * relationship. Do not add NPC footsteps here.
   *
   * ⚠ A phase DISCONTINUITY draws no step. `WalkWobble#reset()` (respawn,
   * teleport, level entry) snaps the phase to 0 from wherever it stood, and the
   * wrap at TAU looks identical to it from here. Both are rejected by the same
   * test — a real step advances ~0.15 rad at `freq` 9 and `dt` 1/60, so anything
   * past a whole footfall in one step is not a gait, it is a jump cut.
   */
  const footsteps = () => {
    const w = player?.wobble;
    if (!w) return;
    const ph = usable(w.phase) ? w.phase : 0;
    let d = ph - lastPhase;
    lastPhase = ph;
    // Not walking: on foot is the only place a footstep exists, and a dead
    // player is not walking either. The accumulator is cleared rather than
    // banked, or a player who drives across town arrives with a step owed.
    if (player?.dead || driving?.inCar || !(w.amp > AUDIO.stepAmp)) {
      stepAcc = 0;
      return;
    }
    if (d < 0) d += TAU; // the phase wraps at TAU
    if (!(d > 0) || d > STEP_PHASE) return; // no movement, or a discontinuity
    stepAcc += d;
    if (stepAcc < STEP_PHASE) return;
    // ⚠ `if`, NOT `while`, and the clamp with it — `Gameplay`'s step 3 rule. A
    // frame hitch that banked three footfalls would fire three footsteps on the
    // recovery frame, which is a burst nobody's legs made.
    stepAcc -= STEP_PHASE;
    if (stepAcc > STEP_PHASE) stepAcc = STEP_PHASE;
    footfalls++;
    const p = player.position;
    shotAt('footstep', p?.x ?? 0, p?.y ?? 0, p?.z ?? 0);
  };

  /**
   * THE IDLING CARS: the player's own whenever they are driving, plus the
   * nearest {@link AUDIO.engineCars} AI cars.
   *
   * ⚠ THE KEY IS THE FLEET HANDLE, and that is what buys the pitch.
   * `car_engine` is `pick: 'sticky'` with `rateKeyed: 0.18`, both hashed off the
   * key, so one shipped engine buffer becomes a street of cars holding audibly
   * different notes — and each car holds ITS note for as long as it exists.
   *
   * ⚠ No accessor was added to `Traffic.js`. The director already exposes its
   * driving pool as public parallel arrays (`VehicleDamage` polls the same ones
   * every step), and a live slot is exactly `handle[i] >= 0` — `_deactivate`
   * sets it to −1 on the way out, so there is no state byte to interpret.
   */
  const engines = () => {
    engineWant.length = 0;

    // THE PLAYER'S OWN CAR is always in the set and is never sorted for: it is
    // the one engine that must be audible whatever else is in the street.
    const mine = driving?.inCar && usable(driving?.fleetHandle) && driving.fleetHandle >= 0
      ? driving.fleetHandle
      : -1;
    if (mine >= 0) {
      const b = driving.body;
      engineWant.push(mine);
      hold('car_engine', mine, { x: b?.x ?? ear.x, y: b?.y ?? ear.y, z: b?.z ?? ear.z });
    }

    // ...and the nearest few AI cars, chosen through a fixed window rather than
    // a sort: a bounded insertion costs no allocation and touches at most
    // `engineCars` slots per candidate.
    const cap = AUDIO.engineCars;
    let live = 0;
    const top = traffic?.top | 0;
    for (let i = 0; i < top; i++) {
      const h = traffic.handle?.[i] ?? -1;
      if (h < 0 || h === mine) continue;
      const cx = traffic.x[i];
      const cz = traffic.z[i];
      const dx = cx - ear.x;
      const dz = cz - ear.z;
      const d2 = dx * dx + dz * dz;
      if (live === cap && d2 >= nearD2[cap - 1]) continue;
      let j = live < cap ? live : cap - 1;
      while (j > 0 && nearD2[j - 1] > d2) {
        nearD2[j] = nearD2[j - 1];
        nearH[j] = nearH[j - 1];
        nearX[j] = nearX[j - 1];
        nearY[j] = nearY[j - 1];
        nearZ[j] = nearZ[j - 1];
        j--;
      }
      nearD2[j] = d2;
      nearH[j] = h;
      nearX[j] = cx;
      nearY[j] = traffic.y?.[i] ?? 0;
      nearZ[j] = cz;
      if (live < cap) live++;
    }
    for (let k = 0; k < live; k++) {
      engineWant.push(nearH[k]);
      hold('car_engine', nearH[k], { x: nearX[k], y: nearY[k], z: nearZ[k] });
    }

    // Stop everything that left the set — a car that despawned, parked, was
    // jacked, or simply fell out of the nearest three.
    for (let k = 0; k < engineKeys.length; k++) {
      if (engineWant.indexOf(engineKeys[k]) < 0) drop('car_engine', engineKeys[k]);
    }
    const swap = engineKeys;
    engineKeys = engineWant;
    swap.length = 0;
    engineWant = swap;
  };

  /** The rotor loop. `drone.active` is the same gate `DroneMesh` draws on. */
  const droneLoop = () => {
    const on = !!drone?.active;
    if (on) {
      hold('drone', 'drone', { x: drone.x, y: drone.y, z: drone.z });
    } else if (droneOn) {
      drop('drone', 'drone');
    }
    droneOn = on;
  };

  /**
   * WHERE THE SIREN IS STANDING — AND WHETHER THERE IS ANYWHERE FOR IT TO
   * STAND AT ALL.
   *
   * ⚠⚠ **A SIREN IS A THING BOLTED TO A CAR, AND THIS USED TO HANG IT ON A
   * PERSON.** It walked `EnemyManager` for the nearest live POLICE slot, and
   * that set is PEOPLE: `Wanted.js#RESPONSE_BUDGET` buys ★1 two foot officers
   * and ZERO cars, so at one star the siren emitted from a policeman strolling
   * down the pavement. Reported by the owner in exactly those words.
   *
   * So the source is now `wanted.response.nearestSiren`, and the set it walks
   * is EXACTLY the set whose lightbar `Wanted.js` is already flashing — the
   * driving cruisers and the crewless ones dumped at a scene. ONE SET, TWO
   * OUTPUTS: the sound is where the lights are, and a siren can never be heard
   * from something the player cannot see flashing. **★1 therefore has NO SIREN,
   * and that is the correct reading, not a gap** — sirens come off cars and ★2
   * is the first car.
   *
   * ⚠⚠ **NO SOURCE MEANS SILENCE, AND THE OLD FALLBACK'S REASONING WAS WRONG.**
   * This function used to leave `bx/by/bz` at the LISTENER whenever the walk
   * resolved nobody, and called that "the plain 2D reading, deliberately not a
   * silent frame". It is nothing of the sort. Distance 0 and centre pan is not
   * a degraded reading of a distant sound, it is **the loudest possible one** —
   * `siren_police` carries the longest reach of any LOOP in the table (120 m)
   * precisely so an arriving cruiser is audible, and the fallback took the one
   * case where there is no cruiser at all and welded the sound to the player's
   * own head, moving with them wherever they went. The window between a crime
   * landing and the first unit spawning hit it every single time. ⚠ The 120 was
   * **160** when this was found, and the two halves of the owner's report are
   * one report: the reach was retuned in `src/game/sfx.js` the same round, and
   * **no `maxDist` on that page could have fixed this** — a sound sited at
   * distance 0 is inside every `refDist` there is.
   *
   * ⚠ THE ASK IS GATED ON THE SIREN. `sirenOn` is the `'wanted:level'` flag, so
   * a quiet city walks nothing at all; and it is duck-typed the whole way down
   * (`wanted?.response?.nearestSiren?.(…)`), so the heat-curve-only
   * construction — no manager, no traffic, hence no `response` — is silent
   * rather than a crash.
   */
  const siren = () => {
    if (!sirenOn) return;
    const found = wanted?.response?.nearestSiren?.(ear.x, ear.z, sirenAt) === true;
    if (!found) {
      // Drop ONCE, not every step: see `sirenPlaced`. A ★1 patrol sits here for
      // its whole life and must not bill the facade a stop per frame.
      if (sirenPlaced) drop('siren_police', 'police');
      sirenPlaced = false;
      return;
    }
    sirenPlaced = true;
    hold('siren_police', 'police', { x: sirenAt.x, y: sirenAt.y, z: sirenAt.z });
  };

  // ── the event half ─────────────────────────────────────────────────────────

  /**
   * `on()` returns its own unsubscriber on `createBus()`; the `off(type, fn)`
   * fallback is for a duck bus in a suite that does not.
   * @param {string} type @param {(p: any) => void} fn
   */
  const sub = (type, fn) => {
    if (!bus?.on) return;
    const off = bus.on(type, fn);
    unsubs.push(typeof off === 'function' ? off : () => bus.off?.(type, fn));
  };

  if (bus?.on) {
    // ── the world ────────────────────────────────────────────────────────────
    sub('car:exploded', (p) => {
      shotAt('explosion', p?.x, p?.y ?? ground(p?.x, p?.z), p?.z);
    });
    // ⚠ THE PACK SHIPS ONE SMASH AND IT IS WOOD, so a felled LAMP takes it too.
    // That is a documented stopgap, not a decision: a lamp column going over in
    // silence while the bench beside it splinters is worse than the wrong
    // material, and the fix is one more asset and one more row in
    // `src/game/sfx.js` — not a branch here.
    sub('prop:broken', (p) => {
      shotAt('prop_smash', p?.x, ground(p?.x, p?.z), p?.z);
    });
    // ⚠ THE BLACK EDGE ONLY. `'screen:fade'` fires twice per door — black going
    // in, clear coming out — and the door is the press, which is the black one.
    //
    // ⚠⚠ **SITED ON THE PLAYER, KEYED ON THE DOOR — AND SITING IT ON THE DOOR
    // MADE THE EXIT SILENT.** It shipped positioned at `interiors.focus`, the
    // shopfront ring, reasoning that "by the time the fade clears the player is
    // 100 m down". That is true and it is the wrong way round: the shot is fired
    // at the PRESS, and at the press the player is exactly where the door is —
    // on the pavement going in, on the exit mat going out. `focus` stays on the
    // STREET for both, so leaving a shop sited the sound 100 m above a listener
    // standing under solid rock. `door.maxDist` is 25 and the mixer's cull is 3D,
    // so it was rejected before it ever allocated a voice. Entering worked
    // perfectly, which is exactly how it went unnoticed.
    //
    // The STICKY KEY still comes off the door ring rounded to the metre, so a
    // given shop's door keeps sounding like that shop's door and — the point —
    // going out sounds like the same door as coming in. Position and identity
    // are different questions and this is the one place they have different
    // answers. A rounded position is a pure derivation: no dice, rule 4 intact.
    sub('screen:fade', (p) => {
      if (!p?.black) return;
      const at = player?.position ?? interiors?.focus ?? null;
      const x = usable(at?.x) ? at.x : 0;
      const y = usable(at?.y) ? at.y : ground(x, usable(at?.z) ? at.z : 0);
      const z = usable(at?.z) ? at.z : 0;
      const ring = interiors?.focus ?? at;
      const kx = usable(ring?.x) ? Math.round(ring.x) : 0;
      const kz = usable(ring?.z) ? Math.round(ring.z) : 0;
      shotAt('door', x, y, z, { key: `${kx}:${kz}` });
    });
    // ⚠ 2D on the `sfx` bus would be wrong and this is not that: `rain` is an
    // `ambient` row in the table and MUST NEVER become positional — it has no
    // source to be at. `Weather` publishes a DAMPED amount at ~5 Hz, so this is
    // a level being tracked, not a switch being thrown; the hysteresis is what
    // stops the bed chattering across the ramp. The bus has no replay, but at
    // 5 Hz the state we miss at construction is at most 0.2 s old.
    sub('weather', (p) => {
      const r = usable(p?.rain) ? p.rain : 0;
      rainAmt = r;
      if (!rainOn && r > AUDIO.rainOn) rainOn = true;
      else if (rainOn && r < AUDIO.rainOff) {
        rainOn = false;
        drop('rain', 'rain');
        return;
      }
      if (rainOn) hold('rain', 'rain', { gain: r });
    });

    // ── the money and the jobs (all `ui`, all 2D) ────────────────────────────
    sub('shop:bought', () => shot('cash'));
    // ⚠ A COMPLETION IS TWO SOUNDS AND THAT IS THE POINT — the money lands, then
    // the job closes. `cash` and `ui_notify` are separate rows on separate
    // `minGap`s, so the pair cannot mask itself.
    sub('mission:event', (p) => {
      if (p?.type === 'complete') shot('cash');
      shot('ui_notify');
    });
    sub('drone:delivered', () => shot('ui_notify'));

    // ── the two sirens ───────────────────────────────────────────────────────
    // The ambulance is the WASTED bed: `ambient`, 2D on purpose (see the table),
    // and started here rather than polled because a dead player's sim keeps
    // running and the splash is an EVENT.
    sub('player:wasted', () => {
      ambulanceOn = true;
      hold('siren_ambulance', 'wasted');
    });
    sub('player:respawn', () => {
      if (!ambulanceOn) return;
      ambulanceOn = false;
      drop('siren_ambulance', 'wasted');
    });
    // ⚠ THE POLICE SIREN IS STARTED BY `update`, NOT HERE, because it is SITED
    // and the car it is sited on moves every step (see `siren`). What the event
    // owns is the flag and the STOP: a wanted level cleared by a respawn must go
    // silent on the instant, not on the next step.
    sub('wanted:level', (p) => {
      const on = usable(p?.level) && p.level > 0;
      if (!on && sirenOn) drop('siren_police', 'police');
      if (!on) sirenPlaced = false;
      sirenOn = on;
    });

    // ── the phone (every row `ui`, so every row survives the freeze) ─────────
    // ⚠ THIS IS THE CASE THE TWO-HOOK SPLIT EXISTS FOR. A ring starts while the
    // sim runs and then the player opens a panel, which freezes the loop — so
    // the ring's fade lives entirely in `render`.
    sub('phone:ring', (p) => {
      const on = usable(p?.waiting) && p.waiting > 0;
      if (on === ringOn) return;
      ringOn = on;
      if (on) hold('phone_ring', 'phone');
      else drop('phone_ring', 'phone');
    });
    sub('phone:open', () => shot('phone_key'));
    sub('phone:view', () => shot('phone_key'));
    sub('phone:order', () => shot('phone_key'));
    sub('phone:callback', () => shot('phone_key'));
    // ⚠ THE FIRST SHOT ONLY. `'phone:call'` is republished once PER SHOT on the
    // same token, so a per-line notify would chirp through the whole
    // conversation — the alert is "somebody is talking to you", which happens
    // once.
    sub('phone:call', (p) => {
      if (p?.shot === 0) shot('text_notify');
    });

    // ── the car turning over, and the window onto the station ────────────────
    // Sticky on the FLEET HANDLE, so a given car always turns over the same way.
    // ⚠ `fleetHandle` is live across the whole two-phase seam (it is written at
    // the REQUEST), so it is already correct when `finishEnter` publishes this.
    //
    // ⚠ ONE HANDLER FOR BOTH, AND NOT ONE NEW CHANNEL. The radio wanted exactly
    // the signal this row already carried, `{in}` — so nothing was published for
    // it, which is the rule at the top of this file, and there is no second
    // subscriber on the channel to fall out of order with the first.
    //
    // ⚠ GETTING OUT IS TWO STATEMENTS AND ONLY THE SECOND IS ABOUT SOUND.
    // `exit()` closes the WINDOW; the transmitter goes on running in `render`,
    // which is what lets a re-entry resume where the track would now be. Fading
    // the stream is a separate fact about this machine's speakers, and collapsing
    // the two would be exactly the "stopping the radio" the feature refuses.
    sub('player:vehicle', (p) => {
      if (p?.in) {
        const b = driving?.body;
        const h = usable(driving?.fleetHandle) ? driving.fleetHandle : 0;
        shotAt('car_start', b?.x ?? ear.x, b?.y ?? ear.y, b?.z ?? ear.z, { key: h });
        // Publishes `'radio:track'` SYNCHRONOUSLY whenever it starts or resumes
        // one, which the subscription below is already waiting on.
        radio?.enter();
        return;
      }
      if (!radio) return;
      radio.exit();
      musicOn = false;
      sfx?.music?.stop?.(musicFade);
    });

    // ⚠ TRANSITIONS ONLY. `Radio.js` publishes this when a track STARTS and when
    // an entry RESUMES one — never per frame — so this is a handful of events an
    // hour, not a poll, and it is safe for it to do real work.
    //
    // ⚠ `t.file` IS THE ADDRESS, VERBATIM. See the header: the model owns the
    // join, this file owns the noise, and the moment both of them know the base
    // one of them is wrong.
    //
    // ⚠ THE GAIN IS PUSHED HERE RATHER THAN ONCE AT CONSTRUCTION, and that is
    // `reassertLoops`'s lesson one layer up: there is no `AudioContext` until the
    // player's first gesture, so a level set before one existed was never set at
    // all — and unlike a loop, a stream that comes back at the wrong level does
    // not heal itself. A track start is the only moment the number matters.
    if (radio) {
      sub('radio:track', (t) => {
        if (disposed) return;
        const m = sfx?.music;
        if (!m) return;
        const url = typeof t?.file === 'string' ? t.file : '';
        if (!url) return;
        const off = usable(t?.offset) && t.offset > 0 ? t.offset : 0;
        if (usable(radioTune?.gain)) m.setGain?.(radioTune.gain);
        musicOn = true;
        radioStarts++;
        m.play?.(url, off, musicOpts);
      });
    }
  }

  const api = {
    name: 'gameAudio',

    /**
     * The chibi sprite decoded and `Gameplay` rebuilt the actor. Same seam as
     * `Interiors#setPlayer` / `Shops#setPlayer`. ⚠ The walk phase goes with it:
     * a fresh `WalkWobble` starts at 0, and reading the old one's phase against
     * it would bank a spurious footfall.
     * @param {any} next
     */
    setPlayer(next) {
      player = next ?? null;
      lastPhase = usable(next?.wobble?.phase) ? next.wobble.phase : 0;
      stepAcc = 0;
    },

    // ── the call sites `Gameplay.js` uses ───────────────────────────────────

    /** The player's auto-attack, on the path where a round really left. */
    playerShot() {
      const p = player?.position;
      shotAt('gunshot', p?.x ?? 0, p?.y ?? 0, p?.z ?? 0);
    },

    /** An NPC's round. @param {number} x @param {number} y @param {number} z */
    npcShot(x, y, z) {
      shotAt('gunshot', x, y, z);
    },

    /**
     * Somebody was hit or killed. ⚠ `groan` carries the loudest `minGap` in the
     * table (0.25) because the three cuts are one actor's single take, so this
     * deliberately does NOT throttle: the mix already does, in one place.
     * @param {number} x @param {number} y @param {number} z
     */
    groanAt(x, y, z) {
      shotAt('groan', x, y, z);
    },

    /**
     * ⚠ THE PLAYER TOOK A ROUND, AND THIS IS WIRED AT `player.hurt`, NOT ON
     * `'player:damage'`. In a car the damage is redirected to the VEHICLE a few
     * lines earlier and the player never bleeds (`docs/vehicles.md`), so a
     * groan on the channel would have the driver crying out at every bullet
     * that hit the bodywork.
     */
    playerHurt() {
      const p = player?.position;
      shotAt('groan', p?.x ?? 0, p?.y ?? 0, p?.z ?? 0);
    },

    /**
     * A crash. ⚠ **THE GATE IS `VehicleDamage`'s OWN ARITHMETIC**, not a
     * threshold invented here: `amt = max(0, dv - min) * damage` is exactly what
     * `onImpact` bills, so a crash you can hear is a crash that cost something
     * and a kerb scrape is silent because it was free. Loudness rides the same
     * number — the crash is as loud as it was expensive — clamped into
     * [{@link AUDIO.crashMinGain}, 1].
     *
     * @param {'wall'|'car'} kind
     * @param {number} dv m/s the impact took off the body.
     */
    carImpact(kind, dv) {
      if (!usable(dv) || dv <= 0) return;
      const wall = kind === 'wall';
      const min = wall ? VEHICLE.wallImpactMin : VEHICLE.carImpactMin;
      const per = wall ? VEHICLE.wallDamage : VEHICLE.carDamage;
      const amt = Math.max(0, dv - min) * per;
      if (amt <= 0) return;
      const full = VEHICLE.maxHp * AUDIO.crashFullFrac;
      const g = Math.max(AUDIO.crashMinGain, Math.min(1, amt / full));
      const b = driving?.body;
      shotAt('car_crash', b?.x ?? ear.x, b?.y ?? ear.y, b?.z ?? ear.z, { gain: g });
    },

    // ── the two hooks ────────────────────────────────────────────────────────

    /**
     * THE SIM-COUPLED HALF. Everything in here describes a world that is moving,
     * and none of it should run while an overlay holds the freeze — see the
     * header. Call it on the fixed step.
     * @param {number} dt Seconds.
     */
    update(dt) {
      if (disposed || !usable(dt) || dt <= 0) return;
      steps++;
      // FIRST, and before any play below: everything positional is evaluated
      // against the listener as it stood when the sound was asked for.
      pushListener();
      footsteps();
      engines();
      droneLoop();
      siren();
    },

    /**
     * THE REAL-TIME HALF. Voice reaping and loop fades, which MUST keep running
     * while the sim is frozen — see the header.
     *
     * ⚠ THE `dt` ARGUMENT IS NOT USED FOR THE AGEING, and that is the whole
     * point: `createLoop` scales it by `timeScale`, so it is exactly 0 under
     * every one of the five freeze owners. The span billed to `sfx` comes off a
     * REAL clock, which is also the clock the `AudioContext` schedules on. `dt`
     * is still taken so the signature matches every other system's and so the
     * very first render (which has no previous timestamp) has something honest
     * to bill.
     *
     * @param {number} dt Seconds since the last render, TIME-SCALED.
     */
    render(dt) {
      if (disposed) return;
      frames++;
      const ms = clock();
      let span;
      if (!usable(ms)) span = usable(dt) ? dt : 0;
      else if (lastMs === null) span = usable(dt) && dt > 0 ? dt : 0;
      else span = (ms - lastMs) / 1000;
      if (usable(ms)) lastMs = ms;
      if (!usable(span) || span < 0) span = 0;
      if (span > AUDIO.maxRenderDt) span = AUDIO.maxRenderDt;
      pushListener();
      // ⚠ BEFORE `sfx.update`, not after: a loop re-asked here should be live
      // for the span we are about to bill, not from the next frame. See
      // `reassertLoops` for why this is here at all — the intro phone ring was
      // silently lost every session without it.
      reassertLoops();
      // ⚠ THE STATION IS TOLD THE SAME REAL SPAN, AND IT IS TOLD IT HERE, in
      // `render`, whether anybody is listening or not and whether the sim is
      // frozen or not. That single line is what makes "resume at 0:40" true; see
      // the header. It may publish `'radio:track'` from inside this call — a
      // track rolling over into the next one — which lands on the subscription
      // above and starts the stream BEFORE the ageing below, exactly like a
      // re-asserted loop and for the same reason.
      radio?.update(span);
      sfx?.update?.(span);
    },

    /** @returns {Object} Live counters for `Gameplay.stats()` and the suites. */
    stats() {
      return {
        steps,
        frames,
        played,
        footfalls,
        // What this file DID, never what the stream is doing — that reading is
        // the facade's (`sfx.music`) and the model's (`radio.stats()`), and a
        // third copy here would be a second book of one number.
        radioStarts,
        engines: engineKeys.length,
        drone: droneOn,
        // ⚠ `sirenPlaced`, NOT `sirenOn` — every other key on this block reports
        // a loop that is HELD, and a readout claiming a siren while a ★1 patrol
        // holds none is a debug panel that lies about the one case this round
        // fixed. `sirenOn` is the WANT and is not a fact about the mixer.
        siren: sirenPlaced,
        ambulance: ambulanceOn,
        ring: ringOn,
        rain: rainOn ? rainAmt : 0,
        subs: unsubs.length,
        listener: { x: ear.x, y: ear.y, z: ear.z, yaw: ear.yaw },
        sfx: sfx?.stats?.() ?? null,
      };
    },

    /**
     * Idempotent. Drops every subscription, then releases every loop it is
     * holding — in that order, so a channel that fires during the teardown
     * cannot start one behind us. `update`/`render` are no-ops afterwards and
     * every play door checks `disposed`, so a system torn down mid-frame cannot
     * make a noise on its way out. The `Wallet#dispose` rule.
     */
    dispose() {
      if (disposed) return;
      for (const off of unsubs) {
        try {
          off();
        } catch {
          /* a bus that tore itself down first is not our problem */
        }
      }
      unsubs.length = 0;
      // Named rather than `stopAllLoops()` with no argument, because this
      // system is not the only thing that may own a loop (a dev scene drives
      // the same facade), and taking somebody else's down is not our business.
      for (let k = 0; k < engineKeys.length; k++) sfx?.stopLoop?.('car_engine', engineKeys[k]);
      engineKeys.length = 0;
      if (droneOn) sfx?.stopLoop?.('drone', 'drone');
      // ⚠ `sirenPlaced`, NOT `sirenOn` — the flag says a siren is WANTED, the
      // latch says one is actually SOUNDING SOMEWHERE. A ★1 patrol has the
      // first without the second, and naming a loop we never started is the
      // blanket-stop this whole block is written the long way round to avoid.
      if (sirenPlaced) sfx?.stopLoop?.('siren_police', 'police');
      if (ambulanceOn) sfx?.stopLoop?.('siren_ambulance', 'wasted');
      if (ringOn) sfx?.stopLoop?.('phone_ring', 'phone');
      if (rainOn) sfx?.stopLoop?.('rain', 'rain');
      // The music with them, under the same named-rather-than-blanket rule and
      // for the same reason — hence the `musicOn` latch rather than a poll of
      // `sfx.music.playing`, which would answer for whoever else is using the
      // facade. Zero fade: the level it belonged to is already going.
      if (musicOn) sfx?.music?.stop?.(0);
      musicOn = false;
      droneOn = false;
      sirenOn = false;
      sirenPlaced = false;
      ambulanceOn = false;
      ringOn = false;
      rainOn = false;
      disposed = true;
    },
  };

  return api;
}
